import React, { useMemo, useState } from "react";
import { AddOutlined, DeleteOutlineOutlined, EditOutlined, RefreshOutlined, VisibilityOutlined } from "@mui/icons-material";
import { Alert, Button, Chip, IconButton, Skeleton, Tooltip } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import StatusChip from "components/shared/StatusChip/StatusChip";
import { usePermission } from "components/RequirePermission/useAccessPermissions";
import { useGetPermissionsCatalogQuery, useGetRolesQuery } from "state/rtkQuery/access/accessQueries";
import { PERMISSIONS } from "utils/accessManagement/permissionKeys";
import { formatRoleLabel } from "utils/accessManagement/roleDisplay";

import CreateRoleDrawer from "./components/CreateRoleDrawer";
import DeleteRoleDialog from "./components/DeleteRoleDialog";
import RoleDetailDrawer from "./components/RoleDetailDrawer";
import RoleEditDrawer from "./components/RoleEditDrawer";

import "./AccessManagementLayout.css";

// Default sort: active roles first, then by priority ascending — LOWER
// priority number means MORE senior (e.g. EnterpriseAdmin=10 outranks
// User=90), confirmed against real GET /roles data, so ascending shows the
// most senior roles first. The API does not document a sort order, so this
// is explicitly client-side over loaded rows.
const sortRoles = (roles) =>
  [...roles].sort((a, b) => {
    const aActive = a.is_active !== false;
    const bActive = b.is_active !== false;
    if (aActive !== bActive) return aActive ? -1 : 1;
    return (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER);
  });

const RolesPage = () => {
  const [detailRole, setDetailRole] = useState(null);
  const [editRole, setEditRole] = useState(null);
  const [deleteRole, setDeleteRole] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const canRead = usePermission(PERMISSIONS.ROLES_READ);
  const canCreate = usePermission(PERMISSIONS.ROLES_CREATE);
  const canUpdate = usePermission(PERMISSIONS.ROLES_UPDATE);
  const canDelete = usePermission(PERMISSIONS.ROLES_DELETE);

  const { data, isLoading, isFetching, isError, refetch } = useGetRolesQuery(
    { perPage: 200 },
    { skip: !canRead }
  );

  // Full catalog of every assignable permission the backend knows about — not
  // just permissions some role already happens to hold (see GET
  // /roles/permissions comment in accessQueries.js).
  const { data: permissionsCatalog } = useGetPermissionsCatalogQuery(undefined, { skip: !canRead });

  const rows = useMemo(() => sortRoles(data?.roles || []), [data]);
  const allPermissions = useMemo(() => permissionsCatalog || [], [permissionsCatalog]);

  const columns = useMemo(
    () => [
      {
        field: "role_name",
        headerName: "Role name",
        flex: 1.2,
        minWidth: 180,
        renderCell: ({ row }) => <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{formatRoleLabel(row.role_name)}</span>,
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Tooltip title={row.description || ""}>
            <span style={{ fontSize: "12px", color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {row.description || "—"}
            </span>
          </Tooltip>
        ),
      },
      {
        field: "priority",
        headerName: "Priority",
        width: 90,
        renderCell: ({ row }) => <span style={{ fontSize: "13px" }}>{row.priority ?? "—"}</span>,
      },
      {
        field: "permissions",
        headerName: "Permissions",
        width: 120,
        renderCell: ({ row }) => (
          <Chip label={(row.permissions || []).length} size="small" sx={{ fontSize: "11px", background: "#f3f4f6", color: "#374151" }} />
        ),
      },
      {
        field: "is_active",
        headerName: "Status",
        width: 100,
        renderCell: ({ row }) => <StatusChip isActive={row.is_active !== false} />,
      },
      {
        field: "updated_date",
        headerName: "Updated",
        width: 140,
        renderCell: ({ row }) => <span style={{ fontSize: "12px", color: "#9ca3af" }}>{row.updated_date || "—"}</span>,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 140,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="view"
            icon={<VisibilityOutlined fontSize="small" sx={{ color: "#6b7280" }} />}
            label="View details"
            showInMenu={false}
            title="View details"
            onClick={() => setDetailRole(row)}
          />,
          <GridActionsCellItem
            key="edit"
            icon={<EditOutlined fontSize="small" sx={{ color: canUpdate ? "#6b7280" : "#d1d5db" }} />}
            label="Edit role"
            showInMenu={false}
            disabled={!canUpdate}
            title={canUpdate ? "Edit role" : `Requires ${PERMISSIONS.ROLES_UPDATE} permission`}
            onClick={() => setEditRole(row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteOutlineOutlined fontSize="small" sx={{ color: canDelete ? "#b91c1c" : "#d1d5db" }} />}
            label="Delete role"
            showInMenu={false}
            disabled={!canDelete}
            title={canDelete ? "Delete role" : `Requires ${PERMISSIONS.ROLES_DELETE} permission`}
            onClick={() => setDeleteRole(row)}
          />,
        ],
      },
    ],
    [canUpdate, canDelete]
  );

  if (!canRead) {
    return (
      <div className="ecr-ops-content">
        <Alert severity="warning">
          You need the <code>{PERMISSIONS.ROLES_READ}</code> permission to view roles.
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <div className="ecr-ops-page-header am-page-header">
        <div className="am-page-header__text">
          <p className="ecr-ops-page-header__eyebrow">Access Management</p>
          <h1 className="ecr-ops-page-header__title">Roles &amp; Permissions</h1>
          <p className="ecr-ops-page-header__sub">Inspect available roles and the permissions each one grants.</p>
        </div>
        <div className="am-page-header__actions">
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={refetch} disabled={isFetching} sx={{ color: "#fff" }}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canCreate ? "" : `Requires ${PERMISSIONS.ROLES_CREATE} permission`}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined />}
                onClick={() => setCreateOpen(true)}
                disabled={!canCreate}
                sx={{ textTransform: "none", backgroundColor: "#86BC25", "&:hover": { backgroundColor: "#76a621" } }}
              >
                Create role
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="ecr-ops-content">
        <p className="am-toolbar__note" style={{ marginBottom: 8 }}>
          Sorted client-side: active roles first, then by priority. The API does not define a canonical sort order.
        </p>

        {isError ? (
          <Alert severity="error" action={<IconButton size="small" onClick={refetch}><RefreshOutlined fontSize="small" /></IconButton>}>
            Failed to load roles.
          </Alert>
        ) : isLoading ? (
          <Skeleton variant="rounded" height={420} />
        ) : (
          <div className="am-grid-wrap" style={{ height: Math.min(600, 88 + rows.length * 56) }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={isFetching}
              rowHeight={56}
              disableVirtualization
              hideFooterSelectedRowCount
              sx={{
                border: "none",
                fontSize: "13px",
                "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F8FAFC", borderBottom: "1px solid #DCE3EA" },
                "& .MuiDataGrid-columnHeaderTitle": { color: "#111827", fontSize: "12px", fontWeight: 700 },
              }}
              localeText={{ noRowsLabel: "No roles found." }}
            />
          </div>
        )}
      </div>

      <RoleDetailDrawer
        role={detailRole}
        canUpdate={canUpdate}
        onClose={() => setDetailRole(null)}
        onEdit={(role) => {
          setDetailRole(null);
          setEditRole(role);
        }}
      />

      <RoleEditDrawer
        role={editRole}
        knownPermissionCatalog={allPermissions}
        allRoles={rows}
        onClose={() => setEditRole(null)}
        onSaved={() => setEditRole(null)}
      />

      <CreateRoleDrawer
        open={createOpen}
        knownPermissionCatalog={allPermissions}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          refetch();
        }}
      />

      <DeleteRoleDialog role={deleteRole} onClose={() => setDeleteRole(null)} />
    </div>
  );
};

export default RolesPage;
